import React, { useState } from 'react';
import I18n from 'i18n-js';

import { RCButton, RCMenu, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import {
  CaretDownIcon,
  CaretUpIcon,
  PencilSimpleIcon,
  MapPinLineIcon,
  TrendUpIcon,
  HandshakeIcon,
  TagIcon,
  BucksIcon,
} from '@reverbdotcom/cadence/icons/react';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import {
  Listing,
  SellerListingCardPaymentSettingsQuery,
  CoreMyListingAvailableSalesMemberships,
  core_apimessages_Listing_SeedType as SeedType,
} from '@reverbdotcom/commons/src/gql/graphql';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';

import URLHelpers, { Paths } from '../shared/url_helpers';
import Location from '../../lib/wrapped_location';
import BumpModalV2WithController from './footer_buttons/BumpButtonV2';
import { bumpRatePercentToDecimal } from '../bump/bump_helpers';
import { AutoOfferModalV2 } from './footer_buttons/AutoOfferModalButtonV2';
import SalesModal, { AVAILABLE_SALES_MEMBERSHIPS_QUERY } from './footer_buttons/sales/SalesModal';
import { AffirmZeroPercentModal } from './footer_buttons/affirm_zero_percent_financing/AffirmZeroPercentButton';
import { SELLER_LISTING_CARD_PAYMENT_SETTINGS_QUERY } from './footer_buttons/PaymentButton';
import SellerListingCardPaymentSettingsModal from './SellerListingCardPaymentSettingsModal';
import { EndModal } from './footer_buttons/EndButton';
import { canViewListingPaymentSetting, SELLER_SITE } from './SellerListingCardActionMenu';
import { LIVE, ORDERED } from './toolbarHelpers';

interface Props {
  listing: Listing;
  hasRecommendation: boolean;
}

export default function SellerListingCardInsightsActionMenu({ listing, hasRecommendation }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBumpModal, setShowBumpModal] = useState(false);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [showAutoOfferModal, setShowAutoOfferModal] = useState(false);
  const [showAffirmZeroPercentModal, setShowAffirmZeroPercentModal] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isEndModalOpen, setIsEndModalOpen] = useState(false);

  const user = useUser();
  const isListingLive = listing.state === LIVE;
  const isSellerSiteExclusive = listing.exclusiveChannel == SELLER_SITE;
  const isEditable = listing.state !== ORDERED || listing.relistable;

  const bumpRate = listing.bumpRate?.rate || 0;
  const isBumpEligible = isListingLive && !isSellerSiteExclusive;
  const isAutoOffersEnabled = listing.autoOffers.id !== '';
  const showSaleButton = isListingLive && !isSellerSiteExclusive && listing.shop.canCurateSales;
  const showAffirmZeroPercentButton = (listing.affirmZeroPercentFinancingConfiguration.isEligible || listing.affirmZeroPercentFinancingConfiguration.isEnabled) && isListingLive;
  const isAffirmEnabled = listing.affirmZeroPercentFinancingConfiguration.isEnabled;
  const showTradeIn = listing.tradeInEligibility?.isEligible && listing.csp?.id;

  const hasAttractBuyersItems = showTradeIn || isBumpEligible || listing.isAutoOfferEligible || showSaleButton || showAffirmZeroPercentButton;

  const { data: paymentData, loading: paymentLoading } = useQuery<SellerListingCardPaymentSettingsQuery>(SELLER_LISTING_CARD_PAYMENT_SETTINGS_QUERY);
  const { myDirectCheckoutProfile } = paymentData || {};
  const walletOptInEnabled = useExpEnabled(experiments.LISTING_LEVEL_WALLET_OPT_IN);
  const showPaymentButton = !paymentLoading && canViewListingPaymentSetting(user, myDirectCheckoutProfile, walletOptInEnabled);

  const {
    data: salesData,
    loading: salesLoading,
  } = useQuery<CoreMyListingAvailableSalesMemberships.Query>(AVAILABLE_SALES_MEMBERSHIPS_QUERY, {
    ssr: false,
    fetchPolicy: 'network-only',
    variables: {
      listingId: listing.id,
    },
    errorPolicy: 'all',
  });

  const { listing: salesListing = {} } = salesData ?? {};
  const { availableSalesMemberships = {} } = salesListing ?? {};
  const { reverbSalesMemberships = [], sellerSalesMemberships = [] } = availableSalesMemberships;
  const inSale = [...reverbSalesMemberships, ...sellerSalesMemberships].some(sale => sale.isListingInSale);
  const salesButtonText = (salesLoading || inSale) ? I18n.t('discovery.listingsManagement.sellerListingCard.sales.manageSales') : I18n.t('discovery.listingsManagement.sellerListingCard.sales.addToASale');

  const sellFormEditListingPath = URLHelpers.sellFormEditListingPath(listing.id, {
    section: 'review',
  });

  const tradeInPath = showTradeIn
    ? Paths.sellerTradeIn.expand({ seed_id: listing.id, seed_type: SeedType.Product })
    : null;

  const buttonVariant = hasRecommendation ? 'transparent' : 'outlined';
  const buttonSize = hasRecommendation ? 'mini' : 'small';

  return (
    <>
      <RCMenu
        isOpen={menuOpen}
        onOpenChange={setMenuOpen}
        anchor={
          <RCButton
            variant={buttonVariant}
            size={buttonSize}
          >
            <RCTextWithIcon
              icon={menuOpen ? <CaretUpIcon /> : <CaretDownIcon />}
              placement="right"
            >
              {I18n.t('discovery.listingsManagement.sellerListingCard.moreListingActions')}
            </RCTextWithIcon>
          </RCButton>
        }
        position="bottom-end"
      >
        {isEditable && (
          <RCMenu.Item
            icon={<PencilSimpleIcon />}
            onClick={() => {
              trackEvent({
                eventName: MParticleEventName.ClickedComponent,
                componentName: 'EditButton',
                listingId: listing.id,
              });
              Location.assign(sellFormEditListingPath);
            }}
          >
            {I18n.t('discovery.listingsManagement.sellerListingCard.edit')}
          </RCMenu.Item>
        )}

        {hasAttractBuyersItems && (
          <>
            <RCMenu.Separator />
            <RCMenu.Group label={I18n.t('discovery.listingsManagement.sellerListingCard.attractBuyers')}>
              {showTradeIn && (
                <RCMenu.Item
                  icon={<MapPinLineIcon />}
                  onClick={() => Location.assign(tradeInPath)}
                >
                  {I18n.t('discovery.tradeIn.sellerListingCardCallout')}
                </RCMenu.Item>
              )}
              {isBumpEligible && (
                <RCMenu.Item
                  icon={<TrendUpIcon />}
                  onClick={() => {
                    setShowBumpModal(true);
                    trackEvent({
                      eventName: MParticleEventName.ClickedComponent,
                      componentName: 'BumpButton',
                      listingId: listing.id,
                    });
                  }}
                >
                  {listing.bumpRate?.rate ? I18n.t('discovery.bumpButton.manageBump') : I18n.t('discovery.listingsManagement.sellerListingCard.promoteWithBump')}
                </RCMenu.Item>
              )}
              {listing.isAutoOfferEligible && (
                <RCMenu.Item
                  icon={<HandshakeIcon />}
                  onClick={() => {
                    setShowAutoOfferModal(true);
                    trackEvent({
                      eventName: MParticleEventName.ClickedComponent,
                      componentName: 'AutoOfferModalButtonV2',
                      listingId: listing.id,
                    });
                  }}
                >
                  {isAutoOffersEnabled
                    ? I18n.t('discovery.listingsManagement.sellerListingCard.offers.autoOffers.autoOffersManage')
                    : I18n.t('discovery.listingsManagement.sellerListingCard.offers.autoOffers.autoOffersEnable')}
                </RCMenu.Item>
              )}
              {showSaleButton && (
                <RCMenu.Item
                  icon={<TagIcon />}
                  onClick={() => {
                    setShowSalesModal(true);
                    trackEvent({
                      eventName: MParticleEventName.ClickedComponent,
                      componentName: 'SalesModalButton',
                      listingId: listing.id,
                    });
                  }}
                >
                  {salesButtonText}
                </RCMenu.Item>
              )}
              {showAffirmZeroPercentButton && (
                <RCMenu.Item
                  icon={<BucksIcon />}
                  onClick={() => {
                    setShowAffirmZeroPercentModal(true);
                    trackEvent({
                      eventName: MParticleEventName.ClickedAffirmZeroPercentFinancingButton,
                      componentName: 'AffirmZeroPercentButton',
                      listingId: listing.id,
                    });
                  }}
                >
                  {isAffirmEnabled
                    ? I18n.t('discovery.listingsManagement.sellerListingCard.affirmZeroPercent.disableButtonText')
                    : I18n.t('discovery.listingsManagement.sellerListingCard.affirmZeroPercent.enableButtonText')}
                </RCMenu.Item>
              )}
            </RCMenu.Group>
          </>
        )}

        {showPaymentButton && (
          <>
            <RCMenu.Separator />
            <RCMenu.Item
              onClick={() => {
                trackEvent({
                  eventName: MParticleEventName.ClickedComponent,
                  componentName: 'PaymentButton',
                  listingId: listing.id,
                });
                setIsPaymentModalOpen(true);
              }}
            >
              {I18n.t('discovery.listingsManagement.sellerListingCard.payment')}
            </RCMenu.Item>
          </>
        )}

        {isListingLive && (
          <>
            <RCMenu.Separator />
            <RCMenu.Item
              destructive
              onClick={() => {
                trackEvent({
                  eventName: MParticleEventName.ClickedComponent,
                  componentName: 'EndButton',
                  listingId: listing.id,
                });
                setIsEndModalOpen(true);
              }}
            >
              {I18n.t('discovery.listingsManagement.sellerListingCard.end')}
            </RCMenu.Item>
          </>
        )}
      </RCMenu>

      <BumpModalV2WithController
        listingId={listing.id}
        bid={bumpRatePercentToDecimal(bumpRate)}
        showModal={showBumpModal}
        setShowModal={setShowBumpModal}
        listing={listing}
      />
      <SalesModal
        data={salesData}
        isModalOpen={showSalesModal}
        onModalClose={() => setShowSalesModal(false)}
      />
      <AutoOfferModalV2
        showModal={showAutoOfferModal}
        setShowModal={setShowAutoOfferModal}
        listingId={listing.id}
      />
      <AffirmZeroPercentModal
        isAffirmEnabled={isAffirmEnabled}
        listingId={listing.id}
        showModal={showAffirmZeroPercentModal}
        setShowModal={setShowAffirmZeroPercentModal}
      />
      {showPaymentButton && (
        <SellerListingCardPaymentSettingsModal
          listing={listing}
          isModalOpen={isPaymentModalOpen}
          setIsModalOpen={setIsPaymentModalOpen}
        />
      )}
      {isListingLive && (
        <EndModal
          listing={listing}
          modalOpen={isEndModalOpen}
          setModalOpen={setIsEndModalOpen}
        />
      )}
    </>
  );
}
